import React, { useEffect } from 'react';
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
} from 'react-router-dom';
import useAuth from './hooks/useAuth';
import LoginPage from './components/LoginPage';
import SignupPage from './components/SignupPage';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import WorkspaceShell from './components/WorkspaceShell';
import LauncherPage from './components/LauncherPage';
import DashboardPage from './components/DashboardPage';
import VerifyEmailPage from './components/VerifyEmailPage';
import SalarySelectionPage from './components/SalarySelectionPage';
import DataAiAddendumPage from './components/legal/DataAiAddendumPage';
import DmcaPage from './components/legal/DmcaPage';
import { authEvents, markMfaSignOut } from './utils/authEvents';
import { useLanguage } from './i18n';
import EnvironmentBadge from './components/EnvironmentBadge';

// Exported for `App.protectedRoute.test.jsx` — the precedence below IS the
// onboarding hard stop (#335/#374/#402), so it is pinned directly rather than
// only through an E2E.
export const ProtectedRoute = ({ token, gate, ready, notReady, children }) => {
    if (!token) {
        // No `returnUrl` (#512 PO review): every fresh sign-in lands on the Main
        // Page (`/home`). Carrying the signed-out location over sent a user who
        // signed out inside a conversation straight back into it. A reload while
        // signed in never reaches here — App holds `auth-loading` until Firebase
        // has restored the session.
        return <Navigate to="/login" replace />;
    }
    // 🛑 UNKNOWN IS NOT "NOTHING OUTSTANDING" (Salary Gate Bypass #402).
    // The onboarding gates below are derived from the backend profile, which
    // arrives asynchronously and can fail. Until it has landed ONCE we do not
    // know whether this account has finished signing up, and a hard stop cannot
    // be built on a guess: rendering `children` while unknown unlocked the whole
    // workspace for the length of every cold page load, and indefinitely
    // whenever the profile read failed (prod 2026-09-23 — the panels' own
    // requests were being refused 403 email_unverified while the workspace was
    // on screen). Holding a neutral loader here is the only answer that cannot
    // flash the WRONG screen in either direction: never a gate in front of an
    // established account, never the app in front of an un-onboarded one.
    if (!ready) return notReady;
    // An account that has not finished signing up gets an ONBOARDING SCREEN
    // instead of whatever it asked for — #335 email verification (step 2), then
    // #374 salary selection (step 3). Rendering them here rather than inside the
    // workspace is what makes both gates structural: no protected route ever
    // mounts for an un-onboarded account, so nothing downstream has to remember
    // to check, and "navigating to any other URL redirects back" holds for every
    // route by construction. The backend refuses those requests independently,
    // so neither half depends on the other.
    if (gate) return gate;
    return children;
};

export default function App() {
    const { token, user, loading, authStable, profileLoaded, profileError, login, loginWithGoogle, signup, resetPassword, logout, refreshToken, refreshProfile } = useAuth();
    const { t } = useLanguage();

    // The profile is what says whether an onboarding step is outstanding. It
    // arrives asynchronously, so these two are only MEANINGFUL once
    // `profileLoaded` is true — which is why `ProtectedRoute` checks that first
    // (#402). Before this they were read while `user` was still null, and a
    // falsy `required` was indistinguishable from "we have not asked yet": the
    // gate that was meant never to flash in front of an established account was
    // instead never shown to an un-onboarded one.
    const needsEmailVerification = !!user?.emailVerification?.required
        && !user?.emailVerification?.verified;
    // #374 — step 3. Ordered AFTER verification so a brand-new account behind
    // both is walked through the sequence the card specifies rather than being
    // asked to pay before it has proven its address.
    //
    // Since #402 this stays true for the whole of a checkout (and forever, if the
    // user walks away): the gate lifts on a CONFIRMED PAYMENT, not on the choice.
    // The screen itself distinguishes "pick a tier" from "confirming your
    // payment" off `awaiting_payment`, which is why it is handed the whole block
    // rather than just a boolean.
    const needsSalaryChoice = !!user?.salarySelection?.required
        && !user?.salarySelection?.chosen;

    let onboardingGate = null;
    if (needsEmailVerification) {
        onboardingGate = (
            <VerifyEmailPage
                email={user?.emailVerification?.email || user?.email}
                onVerified={refreshProfile}
                onLogout={logout}
            />
        );
    } else if (needsSalaryChoice) {
        onboardingGate = (
            <SalarySelectionPage
                salarySelection={user?.salarySelection}
                onChosen={refreshProfile}
                onLogout={logout}
            />
        );
    }

    useEffect(() => {
        const unsubscribe = authEvents.onAuthExpired(async () => {
            if (!authStable) {
                console.log('Ignoring auth expired event - auth not yet stable');
                return;
            }
            // First-line recovery: try to refresh the Firebase ID token
            // (it may just be expired after a sleep/wake cycle). Only if
            // the refresh actually fails do we sign the user out. This is
            // what makes "close the lid, come back later, still logged in"
            // work — Firebase's refresh token lives in IndexedDB for a year.
            try {
                const fresh = await refreshToken();
                if (fresh) {
                    console.log('Auth expired event recovered — token refreshed in place');
                    return;
                }
            } catch (err) {
                console.warn('refreshToken threw during 401 recovery:', err);
            }
            console.log('Auth token expired and refresh failed — signing out');
            logout();
        });
        return unsubscribe;
    }, [logout, authStable, refreshToken]);

    // 2FA (#333): a `401 mfa_required` means this session's token was minted
    // WITHOUT the second factor while the account has 2FA enrolled (a device
    // that was signed in before 2FA was turned on). A refresh only mints another
    // single-factor token, so there is no recovery to attempt — sign out at
    // once (not gated on `authStable`: the answer cannot change) and let the
    // login page run the challenge.
    useEffect(() => authEvents.onMfaRequired(() => {
        console.log('Second factor required for this account — signing out');
        markMfaSignOut();
        logout();
    }), [logout]);

    // Firebase has not resolved the session yet. DISTINCT from `notReady` below:
    // this covers "who is this?", that one covers "has this account finished
    // signing up?". `setLoading(false)` runs at the END of the auth callback —
    // after the profile read — so on a cold load this screen, not `notReady`,
    // is what holds the door while the profile is in flight. Both are testable
    // so a spec can say which window it is in (#402).
    if (loading) {
        return (
            <div className="flex items-center justify-center h-dvh bg-pai-void"
                 data-testid="auth-loading">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white/70"></div>
            </div>
        );
    }

    // What a protected route renders while the profile — and therefore whether
    // this account has finished signing up — is still UNKNOWN (#402). A loader
    // while it is in flight; once the reads have genuinely failed, an honest
    // dead end with a way forward and a way out, rather than an app that quietly
    // behaves as if nothing were outstanding. Both reuse existing i18n keys.
    const notReady = profileError ? (
        <div className="flex flex-col items-center justify-center gap-4 h-dvh bg-pai-void px-6 text-center"
             data-testid="profile-unavailable">
            <div className="text-white/80 text-[15px] max-w-sm">
                {t('commonProfileUnavailable', "We couldn't load your account. Check your connection and try again.")}
            </div>
            <div className="flex items-center gap-3">
                <button type="button" onClick={refreshProfile}
                        data-testid="profile-retry"
                        className="pai-btn-primary px-4 py-2 text-sm">
                    {t('commonRetry', 'Retry')}
                </button>
                <button type="button" onClick={logout}
                        data-testid="profile-logout"
                        className="pai-btn-ghost px-4 py-2 text-sm">
                    {t('logout', 'Logout')}
                </button>
            </div>
        </div>
    ) : (
        <div className="flex items-center justify-center h-dvh bg-pai-void"
             data-testid="onboarding-loading">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white/70"></div>
        </div>
    );

    return (
        <Router>
            <Routes>
                <Route path="/login" element={<LoginPage onLogin={login} onGoogleLogin={loginWithGoogle} />} />
                <Route path="/signup" element={<SignupPage onSignup={signup} onGoogleLogin={loginWithGoogle} />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage onResetPassword={resetPassword} />} />
                {/*
                  The Data & AI Addendum is PUBLIC — outside the auth gate and
                  outside both onboarding gates, like /login. It is incorporated
                  by reference into the iubenda Terms and linked from them, so a
                  reader arriving from the Terms has no account yet (and may
                  never have one); a document you must accept cannot be behind
                  the thing you accept it to use. The path is fixed by that
                  incorporation — see ADDENDUM_PATH in LegalLinks.
                */}
                <Route path="/legal/pai-data-ai-addendum" element={<DataAiAddendumPage />} />
                {/*
                  Copyright / DMCA Notices — PUBLIC for the same reason as the
                  Addendum above: it is what a DMCA filing with the U.S.
                  Copyright Office references, so a rights-holder with no PAI
                  account must be able to reach it. The path is fixed by that
                  filing.
                */}
                <Route path="/dmca" element={<DmcaPage />} />
                {/*
                  Dashboard stays as its own full-page route (monitoring is
                  intentionally separate from the shell per product direction).
                */}
                <Route
                    path="/dashboard"
                    element={
                        <ProtectedRoute token={token} gate={onboardingGate} ready={profileLoaded} notReady={notReady}>
                            <DashboardPage onLogout={logout} />
                        </ProtectedRoute>
                    }
                />
                {/*
                  Everything else runs through WorkspaceShell, which always
                  keeps Main Office alive and overlays panels via URL.
                    /             — chat, no panel
                    /chat/:id     — specific conversation
                    /calendar     — Calendar panel (peek)
                    /calendar?view=split — Calendar panel (split)
                    /expenses, /documents, /workspace — same pattern
                */}
                {/*
                  The launcher — the post-login entry point (design handoff §1).
                  It renders OUTSIDE the shell: no sidebar, twelve tiles over the
                  wallpaper. `/` stays the Main Chat, so the launcher is reached
                  by a fresh sign-in and by the sidebar's PAI logo.
                */}
                <Route
                    path="/home"
                    element={
                        <ProtectedRoute token={token} gate={onboardingGate} ready={profileLoaded} notReady={notReady}>
                            <LauncherPage onLogout={logout} />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/*"
                    element={
                        <ProtectedRoute token={token} gate={onboardingGate} ready={profileLoaded} notReady={notReady}>
                            <WorkspaceShell onLogout={logout} sessionUser={user} />
                        </ProtectedRoute>
                    }
                />
            </Routes>
            <EnvironmentBadge />
        </Router>
    );
}
